import React, { useCallback, useEffect, useMemo, useState } from "react";
import { UserPlus, Search, KeyRound, Pencil } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageTitle, Card, Loading } from "../portal/ui";
import { useFlash, errText } from "../portal/cms/cmsShared";
import { ROLE_LABEL, CMS, Chip, UserFormModal, PasswordModal } from "./UserModals";

const FILTERS = [
  ["all", "Everyone"], ["platform_admin", "Platform Admins"], ["admin", "Lodge Admins"], ["cms", "CMS users"],
  ["member", "Members"], ["inactive", "Deactivated"],
];
const ROLE_CLS = { platform_admin: "bg-[#0B1530] text-gold", admin: "bg-gold/20 text-gold-deep", member: "bg-sapphire-deep/5 text-sapphire-deep/70" };

export default function PlatformUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null);
  const [flashNode, flash] = useFlash();
  const load = useCallback(() => api.get("/platform/users").then(({ data }) => setUsers(data)), []);
  useEffect(() => { load(); }, [load]);

  const list = useMemo(() => (users || []).filter((u) => {
    const t = q.toLowerCase();
    if (t && ![u.name, u.email, u.rank].some((v) => (v || "").toLowerCase().includes(t))) return false;
    if (filter === "all") return true;
    if (filter === "inactive") return u.active === false;
    if (filter === "cms") return (u.cms_roles || []).length > 0;
    return u.role === filter;
  }), [users, filter, q]);

  const patch = async (u, body, msg) => {
    try { await api.patch(`/platform/users/${u.id}`, body); await load(); flash(msg); } catch (e) { flash(errText(e), "error"); }
  };
  const toggleCms = (u, r) => {
    const roles = u.cms_roles || [];
    patch(u, { cms_roles: roles.includes(r) ? roles.filter((x) => x !== r) : [...roles, r] }, `Content roles updated for ${u.name}.`);
  };
  const done = (msg) => { setModal(null); load(); flash(msg); };
  if (!users) return <Loading />;

  return (
    <div data-testid="control-users">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageTitle eyebrow="Platform Administration" title="Users & Roles" sub="The member directory and every account's platform and content permissions." />
        <button onClick={() => setModal({ type: "create" })} className="btn-sapphire mb-6 sm:mb-0" data-testid="control-add-user"><UserPlus size={16} /> Create account</button>
      </div>
      <div className="mb-6 flex flex-wrap items-center gap-2">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`users-filter-${k}`}
            className={`rounded-full px-4 py-1.5 font-sans text-xs font-semibold uppercase tracking-widest transition-colors ${filter === k ? "bg-gold text-sapphire-deep" : "bg-white text-sapphire-deep/70 ring-1 ring-sapphire-deep/10"}`}>{l}</button>
        ))}
        <div className="relative ml-auto w-full sm:w-72">
          <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-stonegrey" />
          <input className="input-lodge !py-2 !pl-10" placeholder="Search name, email, rank" value={q} onChange={(e) => setQ(e.target.value)} data-testid="users-search" />
        </div>
      </div>
      <Card className="overflow-x-auto !p-0">
        <table className="w-full min-w-[900px] font-sans text-sm">
          <thead>
            <tr className="border-b border-sapphire-deep/10 text-left text-[11px] uppercase tracking-widest text-stonegrey">
              <th className="px-6 py-4">Account</th><th className="px-4 py-4">Platform role</th><th className="px-4 py-4">Content roles</th><th className="px-4 py-4">Status</th><th className="px-4 py-4" />
            </tr>
          </thead>
          <tbody>
            {list.map((u) => {
              const self = u.id === me.id;
              const active = u.active !== false;
              return (
                <tr key={u.id} className={`border-b border-sapphire-deep/5 ${active ? "" : "opacity-55"}`} data-testid={`control-user-row-${u.email}`}>
                  <td className="px-6 py-3.5">
                    <p className="font-semibold text-sapphire-deep">{u.name}{self && <span className="ml-2 text-[11px] font-normal text-gold-deep">(you)</span>}</p>
                    <p className="text-xs text-stonegrey">{u.email} · {u.rank}</p>
                  </td>
                  <td className="px-4 py-3.5">
                    <select value={u.role} disabled={self} onChange={(e) => patch(u, { role: e.target.value }, `${u.name} is now ${ROLE_LABEL[e.target.value]}.`)}
                      className={`rounded-full border-0 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide disabled:cursor-not-allowed ${ROLE_CLS[u.role]}`} data-testid={`control-role-${u.email}`}>
                      {Object.entries(ROLE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex gap-1.5">
                      {CMS.map((r) => <Chip key={r} on={u.is_admin || (u.cms_roles || []).includes(r)} disabled={u.is_admin} onClick={() => toggleCms(u, r)} tid={`control-cms-${r}-${u.email}`}>{r}</Chip>)}
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    <button disabled={self} onClick={() => patch(u, { active: !active }, active ? `${u.name} deactivated.` : `${u.name} reactivated.`)} data-testid={`control-active-${u.email}`}
                      className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide disabled:cursor-not-allowed ${active ? "bg-emerald/12 text-emerald" : "bg-garnet/12 text-garnet"}`}>{active ? "Active" : "Deactivated"}</button>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex justify-end gap-1 text-sapphire-deep/60">
                      <button onClick={() => setModal({ type: "edit", user: u })} className="rounded-lg p-2 hover:bg-parchment hover:text-gold-deep" aria-label="Edit" data-testid={`control-edit-${u.email}`}><Pencil size={15} /></button>
                      <button onClick={() => setModal({ type: "password", user: u })} className="rounded-lg p-2 hover:bg-parchment hover:text-gold-deep" aria-label="Reset password" data-testid={`control-password-${u.email}`}><KeyRound size={15} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!list.length && <p className="p-6 font-sans text-sm text-stonegrey">No accounts match.</p>}
      </Card>
      {modal?.type === "create" && <UserFormModal onClose={() => setModal(null)} onSaved={done} />}
      {modal?.type === "edit" && <UserFormModal user={modal.user} onClose={() => setModal(null)} onSaved={done} />}
      {modal?.type === "password" && <PasswordModal user={modal.user} onClose={() => setModal(null)} onSaved={done} />}
      {flashNode}
    </div>
  );
}
